import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    cloneDeep as _cloneDeep,
    has as _has,
    get as _get,
} from 'lodash';
import Moment from 'moment';
import { toast } from 'react-toastify';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
// import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Select from '@mui/material/Select';
import InputBase from '@mui/material/InputBase';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';

import DeleteIcon from '@mui/icons-material/Delete';
import MenuIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PartialSearchIcon from '@mui/icons-material/ManageSearch';
import ExactSearchIcon from '@mui/icons-material/FindInPage';

import Push from '../../../components/push/';
// import Searchbar from '../../../components/searchbar';
import ConfirmationDialog from '../../../components/confirmationdlg';

import TIService, { tiFields } from '../../../data/ti-service';
import Auth, { ProfileContext } from '../../../data/auth-service';

import HighlightText from '../../../components/highlightText';
import useWindowSize from '../../../libraries/usewindowsize';
import { LoadingOverlay, NoRowsOverlay } from '../../../components/xgrid/overlays';

const useStyles = makeStyles(theme => ({
    statBox: {
        background: theme.styled.paper.background,
        padding: theme.spacing(1),
        margin: '0 8px',
        marginRight: theme.spacing(1),
        textAlign: 'center',
        '& .MuiBox-root': {
            marginTop: theme.spacing(1),
            fontSize: '24px',
        }
    },
    textPrimary: {
        color: theme.palette.primary.main,
    },
    tableHeader: {
        border: '1px solid rgba(255, 255, 255, 0.3)',
        borderBottom: '0px',
        paddingBottom: theme.spacing(1),
        borderRadius: '2px',
        marginBottom: '-1px',
    },
    select: {
        minWidth: 130,
    },
    searchRoot: {
        padding: '2px 4px',
        display: 'inline-flex',
        alignItems: 'center',
        width: 450,
        height: '36.5px',
    },
    input: {
        paddingLeft: theme.spacing(1),
        flex: 1,
    },
    listItem: {
        color: theme.palette.text.hint,
        fontSize: 12,
    },
    iconButton: {
        padding: 8,
    },
    divider: {
        height: 28,
        margin: '0 4px',
    },
}));

const UPDATER = {
    search: '',
    exact: false,
    partial: false,
    indicators: {},
    selector: {},
};

const FILTER = {
    search: '',
    exact: false,
    partial: false,
    sort: 'indicator',
    order: 'asc',
    offset: 0,
    selector: {},
};

const ThreatIntel = () => {
    const classes = useStyles();
    const theme = useTheme();
    const params = useParams();
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const tiPerms = Auth.permissions.get('ThreatIntelPolicy');
    const tiiPerms = Auth.permissions.get('ThreatIntelIndicators');
    const [readonly] = useState(!tiPerms.write);
    const tableAPIRef = useApiRef();
    const { height } = useWindowSize();
    const [shadow, setShadow] = useState(null);
    const [ti, setTI] = useState({ indicators: [], total: 0 });
    const [mode, setMode] = useState('zeek_indicators');
    const [columnWidth, setColumnWidth] = useState({});
    const [sortModel, setSortModel] = useState([{
        field: 'indicator',
        sort: 'asc',
    }]);
    const [filter, setFilter] = useState(_cloneDeep(FILTER));
    const [deleteConfirmation, setDeleteConfirmation] = useState(false);
    const [selectedIndicator, setSelectedIndicator] = useState(false);
    const [tiMenuAnchor, setTIMenuAnchor] = useState(null);
    const [updater, setUpdater] = useState(_cloneDeep(UPDATER));
    const [init, setInit] = useState(true);
    const [search, setSearch] = useState('');
    const [searchField, setSearchField] = useState('all');
    const [searchPlaceholder, setSearchPlaceholder] = useState('Search TI Indicators');
    const [pushing, setPushing] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const searchRef = useRef();

    useEffect(() => {
        initiate();
        setColumnWidth(getProfile('tifeeds').widths || {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setFilter({
            ...filter,
            sort: sortModel[0].field,
            order: sortModel[0].sort,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortModel]);

    const filterTimeout = useRef();
    useEffect(() => {
        clearTimeout(filterTimeout.current);
        filterTimeout.current = setTimeout(() => {
            if (shadow?.uuid) {
                getTI(shadow.uuid, filter);
            }
        }, 500);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const updaterTimeout = useRef();
    useEffect(() => {
        if (!init && shadow) {
            clearTimeout(updaterTimeout.current);
            updaterTimeout.current = setTimeout(() => {
                handleUpdateShadow();
            }, 1000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updater]);

    useEffect(() => {
        setFilter({
            ...buildUpdater(true),
            offset: 0,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, searchField]);

    const initiate = () => {
        checkShadowPolicy((sh, create) => {
            if (sh) {
                setTimeout(() => {
                    setInit(false);
                    setShadow({ uuid: sh.uuid });
                    if (create) {
                        getTI(sh.uuid, filter);
                    }
                });
            }
        });
    };

    const handleUpdateShadow = () => {
        TIService.updateShadowPolicy(params.policy, shadow.uuid, buildUpdater()).then(res => {
            if (!res?.policy?.indicators) {
                res.policy.indicators = [];
            }
        }, err => {
            console.error(err);
        });
    };

    const checkShadowPolicy = (cb) => {
        TIService.checkShadowPolicy(params.policy).then(res => {
            if (res?.uuid) {
                cb(res);
                getTI(res.uuid, filter);
            }
        }, () => {
            TIService.createShadowPolicy(params.policy).then(r => {
                cb(r, true);
            }, err => {
                console.error('Unable to create shadow policy ::: ', err);
            });
            cb(undefined);
        });
    };

    const getTI = (uuid, f) => {
        setLoading(true);
        TIService.getShadowPolicy(params.policy, uuid, f).then(res => {
            if (!res?.policy?.indicators) {
                res.policy.indicators = [];
            }
            // res.policy.indicators = res.policy.indicators.map(i => ({ ...i, id: Helpers.UniqueID() }));
            if (filter.offset !== 0) {
                setTI({
                    ...res.policy,
                    indicators: Array.prototype.concat(ti.indicators, res.policy.indicators),
                });
            } else {
                setTI(res.policy);
            }
        }).finally(() => setLoading(false));
    };

    const buildUpdater = (wantsFilter) => {
        const u = wantsFilter ? { ...filter } : { ...updater };
        if (searchField !== 'all') {
            u.search = '';
            u.selector[searchField] = search;
        } else {
            u.search = search;
            delete u.selector.indicator;
            delete u.selector.indicator_type;
            delete u.selector.ti_source;
            delete u.selector.filename;
            // delete u.selector['meta.desc'];
        }
        u.exact = filter.exact;
        u.partial = filter.partial;

        return u;
    };

    const tableRenderers = {
        indicator: d => (<div><HighlightText text={d.row.indicator} search={searchRef?.current}></HighlightText></div>),
        indicator_type: d => (<div><HighlightText text={d.row.indicator_type} search={searchRef?.current}></HighlightText></div>),
        ti_source: d => (<div><HighlightText text={d.row.ti_source} search={searchRef?.current}></HighlightText></div>),
        filename: d => (<div><HighlightText text={d.row.filename} search={searchRef?.current}></HighlightText></div>),
        'meta.desc': d => (<div><HighlightText text={d.row['meta.desc']} search={searchRef?.current}></HighlightText></div>),
        'meta.if_in': d => (<div><HighlightText text={d.row['meta.if_in']} search={searchRef?.current}></HighlightText></div>),
        // 'meta.do_notice': d => (<div>{d.row['meta.do_notice'] ? 'True' : 'False'}</div>),
        created: d => (<div>{Moment(d.row.created).format(theme.date.long)}</div>),
        updated: d => (<div>{Moment(d.row.updated).format(theme.date.long)}</div>),
        enabled: d => {
            let enabled = d.row.enabled;

            if (_has(updater.indicators, d.row.UUID)) {
                enabled = updater.indicators[d.row.UUID];
            }

            return (
                <FormControlLabel
                    control={
                        <Checkbox
                            disabled={readonly}
                            checked={enabled}
                            onClick={(e) => { handleTIEnable(e, d.row) }}
                        />
                    }
                />
            );
        },
        actions: d => {
            return (
                <Box>
                    {tiiPerms.delete && <IconButton color="secondary" onClick={() => { handleDeleteIndicator(d.row) }}><DeleteIcon /></IconButton>}
                </Box>
            );
        },
    };

    const columns = tiFields.map(f => {
        return {
            ...f,
            width: columnWidth[f.headerName] || 0,
            ...(!columnWidth[f.headerName] ? { flex: 1 } : {}),
            disableColumnMenu: true,
            field: f.variable,
            renderCell: tableRenderers[f.variable],
        };
    });

    const handleTIEnable = (e, indicator) => {
        const u = { ...updater };
        u.indicators[indicator.UUID] = e.target.checked;
        setUpdater(u);
    };

    const handleUpdateSort = s => {
        setSortModel(s.length ? s : [{
            ...sortModel[0],
            sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc',
            offset: 0,
        }]);
    };

    const handleColumnResize = params => {
        const widths = tableAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});

        updateProfile('ti', {
            ...getProfile('ti'),
            widths: widths,
        });
        setColumnWidth(widths);
    };

    const handleColumnReorder = () => {
        let order = [];
        setTimeout(() => {
            order = tableAPIRef.current.getAllColumns().reduce((a, c) => {
                a.push(c.headerName);
                return a;
            }, []);

            updateProfile('ti', {
                ...getProfile('ti'),
                columns: order,
            });
        });
    };

    const handleSearch = (e) => {
        setSearch(e.target.value);
        searchRef.current = e.target.value;
    };

    const pageSize = 50;
    const handleRowsScrollEnd = () => {
        const currentOffset = filter.offset;

        // Stop if  we've already loaded everything
        if (ti.indicators !== null && currentOffset >= ti.indicators.length) {
            return;
        }

        // Stop if the last page fetched had fewer than pageSize records
        if (ti.indicators.length < currentOffset + pageSize) {
            return;
        }

        setFilter(prev => ({
            ...prev,
            offset: prev.offset + pageSize,
        }));
    };

    const handleDeleteIndicator = (d) => {
        if (!d.policies) {
            TIService.GetIndicatorEnabledPolicies(d).then(res => {
                // defense against the dark arts of delete dialog closed before policies are fetched
                setSelectedIndicator(current => {
                    return d ? { ...d, policies: _get(res, 'policies', []) } : current;
                });
            });
        }

        setTimeout(() => {
            setSelectedIndicator(d);
            setDeleteConfirmation(true);
        });
    };

    const handleDelete = () => {
        TIService.deleteTI([{
            indicator: selectedIndicator.indicator,
            'meta.if_in': selectedIndicator['meta.if_in'],
            ti_source: selectedIndicator.ti_source,
        }]).then(res => {
            toast.success('Indicator deleted successfully');
            // refresh the table
            setFilter(current => ({
                ...current,
                offset: 0,
            }));
        }, err => {
            toast.error('Unable to delete the selected indicator');
            console.error('Unable to delete the selected indicator ::: ', err);
        });
        setDeleteConfirmation(false);
    };

    const handleCloseDeleteConfirmation = () => {
        setDeleteConfirmation(false);
    };

    // potentially long operation. will push then assign asynchronously
    const handleDeployPolicy = (sensorList) => {
        setPushing(true);
        TIService.saveShadowPolicy(params.policy, sensorList, shadow.uuid);
        toast.info('Threat Intel Indicator policy update in progress, you will be notified when the operation is complete');

        setMode('zeek_indicators');
        setPushing(false);
        // this policy needs to be disabled until no longer in pending state
        navigate('/policies/zeek/zeek_indicators');
    };

    const handleRevert = () => {
        setTimeout(() => {
            setShadow(null);
            setUpdater(_cloneDeep(UPDATER));
            setFilter(_cloneDeep(FILTER));
            setInit(true);
        });
        TIService.deleteShadowPolicy(params.policy).then(res => {
            initiate();
        }, err => {
            console.error('Unable to delete shadow policy ::: ', err);
        });
    };

    const handleEnabledFilter = (all, enabled) => {
        const f = { ...filter };

        f.offset = 0;
        if (all) {
            delete f.selector.enabled;
        } else if (!all && enabled) {
            f.selector.enabled = true;
        } else if (!all && !enabled) {
            f.selector.enabled = false;
        }

        setFilter(f);
    };

    const handleEnableDisableAll = (enable) => {
        const u = { ...updater };

        u.offset = 0;
        if (enable) {
            u.enable = true;
        } else {
            u.enable = false;
        }

        setUpdater(u);
        handleTIMenuClose();
    };

    const handleTIMenuOpen = (e) => {
        setTIMenuAnchor(e.target);
    };

    const handleTIMenuClose = () => {
        setTIMenuAnchor(null);
    };

    const handleSearchFieldChange = (e) => {
        setSearchField(e.target.value);
    };

    const haveSearch = useMemo(() => {
        return !!filter.search || Object.values(filter.selector).some(v => !!v);
    }, [filter]);

    const handleSearchType = (searchType) => {
        const f = { ...filter };
        const p = 'Partial text search TI Indicators';
        const e = 'Exact match search TI Indicators';
        let s = 'Search TI Indicators';
        if (searchType === 'partial') {
            if (f.exact) {
                s = p;
                f.partial = true;
                f.exact = false;
            } else if (f.partial) {
                f.partial = false;
            } else {
                s = p;
                f.partial = true;
            }
        } else if (searchType === 'exact') {
            if (f.partial) {
                s = e;
                f.exact = true;
                f.partial = false;
            } else if (f.exact) {
                f.exact = false;
            } else {
                s = e;
                f.exact = true;
            }
        }

        setTimeout(() => {
            setFilter(f);
            setSearchPlaceholder(s);
        });
    };

    return (
        <Grid container>
            <Grid item xs={12} style={{ display: mode === 'push' ? 'block' : 'none' }}>
                {tiPerms.admin && <Push
                    policyName={params.policy}
                    policyType={'zeek_indicators'}
                    onDeploy={handleDeployPolicy}
                    closePush={() => { setMode('zeek_indicators') }}
                    pushing={pushing}
                    permName={'ThreatIntelPolicy'}
                />}
            </Grid>
            <Grid container item xs={12} style={{ display: mode === 'zeek_indicators' ? 'flex' : 'none' }}>
                <Grid item xs={10}>
                    {/* <TextField
                        style={{ display: 'inline-flex' }}
                        value={filter.search}
                        onChange={handleSearch}
                    /> */}
                    <Paper component="form" className={classes.searchRoot}>
                        <InputBase
                            className={classes.input}
                            value={search}
                            onChange={handleSearch}
                            placeholder={searchPlaceholder}
                            inputProps={{ 'aria-label': 'Search TI Indicators', spellCheck: false }}
                        />
                        <Divider className={classes.divider} orientation="vertical" />
                        <IconButton
                            className={classes.iconButton}
                            color="default"
                            disabled={!haveSearch}
                            aria-label="clear"
                            onClick={() => { handleSearch({ target: { value: '' } }) }}
                            size="large">
                            <ClearIcon />
                        </IconButton>
                        <IconButton
                            className={classes.iconButton}
                            disabled={!haveSearch}
                            aria-label="search"
                            onClick={() => { handleSearch({ target: { value: filter.search } }) }}
                            size="large">
                            <SearchIcon />
                        </IconButton>
                        <ButtonGroup>
                            <Tooltip title="Partial text search">
                                <Button variant={filter.partial ? 'contained' : 'text'} onClick={() => { handleSearchType('partial') }}><PartialSearchIcon /></Button>
                            </Tooltip>
                            <Tooltip title="Exact search">
                                <Button variant={filter.exact ? 'contained' : 'text'} onClick={() => { handleSearchType('exact') }}><ExactSearchIcon /></Button>
                            </Tooltip>
                        </ButtonGroup>
                    </Paper>
                    <Box ml={2} display="inline-flex">
                        <Select className={classes.select} variant="standard" onChange={handleSearchFieldChange} value={searchField}>
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="indicator">Field Indicator</MenuItem>
                            <MenuItem value="indicator_type">Indicator Type</MenuItem>
                            <MenuItem value="ti_source">Source</MenuItem>
                            <MenuItem value="filename">Filename</MenuItem>
                            {/* <MenuItem value="meta.desc">Description</MenuItem> */}
                        </Select>
                    </Box>
                    {/* <Searchbar style={{ display: 'inline-flex' }} immediate onSearch={handleSearch} searchservice={TIService} value={filter.search} /> */}
                    <Box display="inline-flex" className={classes.statBox}>
                        <Typography variant="body2">Total Indicators: <span className={classes.textPrimary}>{ti.total}</span></Typography>
                    </Box>
                    {/* <Box display="inline-flex" className={classes.statBox}>
                        <Typography variant="body2">Enabled: <span className={classes.textPrimary}>{ti.enabled}</span></Typography>
                    </Box> */}
                </Grid>
                <Grid container item xs={2} justifyContent={'flex-end'} spacing={2}>
                    {tiPerms.admin && <Grid item><Button disabled={readonly} variant="contained" color='primary' aria-label='push' onClick={() => { setMode('push') }}>Push</Button></Grid>}
                    <Grid item><Button disabled={readonly} variant="contained" color='secondary' aria-label='revert' onClick={handleRevert}>Revert</Button></Grid>
                </Grid>
                <Grid item xs={12}>
                    <Box pt={2} height={height - 240} width="100%">
                        <Box className={classes.tableHeader}>
                            <Box display="flex" justifyContent="flex-end">
                                <Box mt={1} mr={2}>
                                    <ButtonGroup>
                                        <Button variant={!_has(filter.selector, 'enabled') ? 'contained' : 'outlined'} color="secondary" aria-label='all filter' onClick={() => { handleEnabledFilter(true) }}>All</Button>
                                        <Button variant={_has(filter.selector, 'enabled') && filter.selector.enabled ? 'contained' : 'outlined'} aria-label='enabled filter' color="secondary" onClick={() => { handleEnabledFilter(false, true) }}>Enabled</Button>
                                        <Button variant={_has(filter.selector, 'enabled') && !filter.selector.enabled ? 'contained' : 'outlined'} aria-label='disabled filter' color="secondary" onClick={() => { handleEnabledFilter(false, false) }}>Disabled</Button>
                                    </ButtonGroup>
                                </Box>
                                <Box mt={1} mr={1}>
                                    {tiPerms.write && <IconButton size="small" aria-label='enable menu' onClick={handleTIMenuOpen}><MenuIcon /></IconButton>}
                                    <Menu
                                        anchorEl={tiMenuAnchor}
                                        open={Boolean(tiMenuAnchor)}
                                        onClose={handleTIMenuClose}
                                    >
                                        <MenuItem onClick={() => { handleEnableDisableAll(true) }}>Enable all in selection</MenuItem>
                                        <MenuItem onClick={() => { handleEnableDisableAll(false) }}>Disable all in selection</MenuItem>
                                    </Menu>
                                </Box>
                            </Box>
                        </Box>
                        <DataGridPro
                            components={{
                                LoadingOverlay: LoadingOverlay,
                                NoRowsOverlay: NoRowsOverlay,
                            }}
                            apiRef={tableAPIRef}
                            rows={ti.indicators}
                            loading={loading}
                            columns={(() => {
                                const p = getProfile('ti');
                                let c = columns;

                                if (p.columns && p.columns.length) {
                                    c = p.columns.map(v => {
                                        for (let i = c.length; i--;) {
                                            if (v === c[i].headerName) {
                                                return c[i];
                                            }
                                        }
                                        return null;
                                    });
                                }

                                return c;
                            })()}
                            hideFooter
                            disableMultipleSelection
                            disableColumnMenu
                            disableColumnFilter
                            disableVirtualization
                            sortModel={sortModel}
                            onSortModelChange={handleUpdateSort}
                            onColumnResize={handleColumnResize}
                            onColumnOrderChange={handleColumnReorder}
                            onRowsScrollEnd={handleRowsScrollEnd}
                            getRowId={d => d.UUID}
                        />
                    </Box>
                </Grid>
            </Grid>
            <ConfirmationDialog
                open={deleteConfirmation}
                message={
                    <React.Fragment>
                        <Typography>
                            Are you sure you would like to delete indicator
                        </Typography>
                        <Box mt={1} ml={2}>
                            <Typography color="secondary">{selectedIndicator.indicator}</Typography>
                        </Box>

                        {!selectedIndicator.policies && <React.Fragment>
                            <Box mt={2}>
                                Counting policies...
                                <CircularProgress size={20} />
                            </Box>
                        </React.Fragment>}
                        {selectedIndicator.policies && !!selectedIndicator.policies.length && <React.Fragment>
                            <Box mt={2}>
                                <Typography>This indicator is currently enabled in the policies:</Typography>
                            </Box>
                            <Box mt={1} ml={2}>
                                {selectedIndicator.policies.map((v, i) =>
                                    <Typography key={'other-policy-' + i} color="secondary">
                                        {v}
                                    </Typography>
                                )}
                            </Box>
                        </React.Fragment>}
                        {selectedIndicator.policies && !selectedIndicator.policies.length && <React.Fragment>
                            <Box mt={2}>
                                <Typography>This indicator is not currently enabled in any policies</Typography>
                            </Box>
                        </React.Fragment>}

                    </React.Fragment>
                }
                onOK={handleDelete}
                onClose={handleCloseDeleteConfirmation}
            />
        </Grid>
    );
};

export default ThreatIntel;
